import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, FileText, Users, BarChart3, User, LogOut, X, HelpCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const userMenuItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/questionari', icon: FileText, label: 'Questionari' },
    { path: '/statistiche', icon: BarChart3, label: 'Statistiche' },
    { path: '/guida', icon: HelpCircle, label: 'Guida Rapida' },
    { path: '/profilo', icon: User, label: 'Profilo' },
  ];

  const adminMenuItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/questionari', icon: FileText, label: 'Questionari' },
    { path: '/utenti', icon: Users, label: 'Gestione Account' },
    { path: '/statistiche', icon: BarChart3, label: 'Statistiche' },
    { path: '/guida', icon: HelpCircle, label: 'Guida Rapida' },
    { path: '/profilo', icon: User, label: 'Profilo' },
  ];

  const menuItems = (user?.role === 'admin' || user?.role === 'super_admin') ? adminMenuItems : userMenuItems;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={toggleSidebar}
          data-testid="sidebar-overlay"
          role="presentation"
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-[280px] bg-white border-r border-slate-100 flex flex-col z-40 transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        data-testid="sidebar"
        role="navigation"
        aria-label="Menu principale"
        aria-hidden={!isOpen && window.innerWidth < 1024 ? 'true' : 'false'}
      >
        {/* Mobile Close Button */}
        <button
          onClick={toggleSidebar}
          className="lg:hidden absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3BA99C] z-10"
          data-testid="sidebar-close-btn"
          aria-label="Chiudi menu di navigazione"
        >
          <X className="w-5 h-5 text-slate-600" aria-hidden="true" />
        </button>

        {/* Scrollable Content */}
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Logos Section */}
          <div className="p-5 border-b border-slate-100 flex-shrink-0">
            {/* Main Logo e-CAA - Larger */}
            <Link 
              to="/dashboard" 
              className="block mb-4 focus:outline-none focus:ring-2 focus:ring-[#3BA99C] rounded" 
              data-testid="logo-ecaa" 
              aria-label="Vai alla Dashboard"
            >
              <div className="flex justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_info-security-fdl/artifacts/0n58s6iv_LOGO_e-CAA_light.png"
                  alt="Logo e-CAA - Centro di Assistenza Agricola"
                  className="h-20 w-auto"
                />
              </div>
            </Link>
          
          {/* Federlombarda logo - Extended version, smaller */}
          <div className="flex justify-center pt-3 border-t border-slate-50">
            <a
              href="https://www.federlombardaagricoltori.it"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="logo-federlombarda"
              aria-label="Vai al sito Federlombarda (si apre in una nuova finestra)"
              className="opacity-80 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#3BA99C] rounded"
            >
              <img
                src="/logo-federlombarda.jpg"
                alt="Logo Federlombarda Agricoltori"
                className="h-6 w-auto"
              />
            </a>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1" aria-label="Navigazione principale">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3.5 text-base font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#3BA99C] ${
                  active
                    ? 'bg-[#F5FBF9] text-[#3BA99C] font-semibold border-r-2 border-[#3BA99C]'
                    : 'text-slate-700 hover:bg-[#F5FBF9] hover:text-[#3BA99C]'
                }`}
                data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="w-5 h-5" aria-hidden="true" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-slate-100">
          <div className="mb-3 px-4 py-2" aria-label="Informazioni utente">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1" id="user-label">
              Utente
            </p>
            <p className="text-base font-semibold text-slate-800">{user?.full_name}</p>
            <p className="text-sm text-slate-500">{user?.email}</p>
            {user?.role === 'super_admin' && (
              <span className="inline-block mt-2 px-2.5 py-1 text-xs font-semibold bg-red-600 text-white rounded" role="status">
                Super Admin
              </span>
            )}
            {user?.role === 'admin' && (
              <span className="inline-block mt-2 px-2.5 py-1 text-xs font-semibold bg-[#5941F5] text-white rounded" role="status">
                Admin
              </span>
            )}
            {/* Show selected office for non-admin users */}
            {user?.role !== 'admin' && user?.role !== 'super_admin' && user?.selected_office_name && (
              <div className="mt-3 p-2 bg-[#F5FBF9] rounded-lg border border-[#3BA99C]/20">
                <p className="text-xs font-semibold text-[#3BA99C] uppercase tracking-wider mb-0.5">
                  Ufficio CAA
                </p>
                <p className="text-sm font-medium text-slate-800">{user.selected_office_name}</p>
                <p className="text-xs text-slate-500 font-mono">{user.selected_office_code}</p>
              </div>
            )}
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 text-base font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
            data-testid="logout-btn"
            aria-label="Esci dall'applicazione"
          >
            <LogOut className="w-5 h-5" aria-hidden="true" />
            Esci
          </button>
        </div>

        {/* Footer - Privacy, Terms & Powered by */}
        <div className="px-4 pb-5 border-t border-slate-100">
          {/* Privacy & Terms Links - Centered and smaller */}
          <div className="flex items-center justify-center py-3">
            <Link
              to="/privacy-policy"
              className="text-xs text-slate-500 hover:text-[#3BA99C] transition-colors"
              data-testid="privacy-policy-link"
            >
              Politica Privacy
            </Link>
            <span className="text-slate-300 mx-2">|</span>
            <Link
              to="/termini-condizioni"
              className="text-xs text-slate-500 hover:text-[#3BA99C] transition-colors"
              data-testid="terms-link"
            >
              Termini e Condizioni
            </Link>
          </div>
          
          {/* Powered by Etom - with bottom spacing */}
          <div className="flex flex-col items-center pt-2 border-t border-slate-50">
            <span className="text-xs text-slate-400 mb-1">Powered by</span>
            <a
              href="https://www.etom.it"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="logo-etom"
              aria-label="Vai al sito Etom (si apre in una nuova finestra)"
              className="opacity-70 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#3BA99C] rounded"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_info-security-fdl/artifacts/xabqsord_LOGO_BIG_GRADIENT_PRINT_No%20sfondo_No%20Payload_Light.png"
                alt="Logo Etom"
                className="h-5 w-auto"
              />
            </a>
          </div>
        </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
